import React, { useEffect, useMemo, useState, useRef } from "react";
import { FiSearch, FiRefreshCw, FiSliders } from "react-icons/fi";
import "./PropertiesFilterBar.css";
import apis from "../../utils/apis";
import httpAction from "../../utils/httpAction";

const TYPES = ["All", "Residential", "Plot", "Commercial"];
const BEDS = ["Any", "Studio", "1", "2", "3", "4", "5+"];
const SORTS = ["Newest", "Price: Low to High", "Price: High to Low"];

export default function PropertiesFilterBar({ onSearch, currentFilters }) {
  const [q, setQ] = useState(currentFilters?.q || "");
  const [location, setLocation] = useState(currentFilters?.location || "All");
  const [type, setType] = useState(currentFilters?.type || "All");
  const [minPrice, setMinPrice] = useState(currentFilters?.minPrice || "");
  const [maxPrice, setMaxPrice] = useState(currentFilters?.maxPrice || "");
  const [beds, setBeds] = useState(currentFilters?.beds || "Any");
  const [sort, setSort] = useState(currentFilters?.sort || "Newest");
  const [locations, setLocations] = useState([]);

  

  // Ref to scroll to the next section after the filter
  const sectionRef = useRef(null);

  const payload = useMemo(
    () => ({ q, location, type, minPrice, maxPrice, beds, sort }),
    [q, location, type, minPrice, maxPrice, beds, sort]
  );

  const reset = () => {
    setQ("");
    setLocation("All");
    setType("All");
    setMinPrice("");
    setMaxPrice("");
    setBeds("Any");
    setSort("Newest");
    onSearch?.({
      q: "",
      location: "All",
      type: "All",
      minPrice: "",
      maxPrice: "",
      beds: "Any",
      sort: "Newest",
    });
  };

  const submit = (e) => {
    e.preventDefault();
    onSearch?.(payload);

    // Scroll to the section after filter
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const getLocationList = async () => {
    const data = {
      url: apis().getLocationList,
    };
    const result = await httpAction(data);
    if (result?.status) {
      const formatedList = result?.list?.map((item) => item?.title);

      setLocations(formatedList);
    }
  };

  useEffect(() => {
    getLocationList();
  }, []);

  useEffect(() => {
    if (!currentFilters) return;
    setQ(currentFilters.q || "");
    setLocation(currentFilters.location || "All");
    setType(currentFilters.type || "All");
    setMinPrice(currentFilters.minPrice || "");
    setMaxPrice(currentFilters.maxPrice || "");
    setBeds(currentFilters.beds || "Any");
    setSort(currentFilters.sort || "Newest");
  }, [currentFilters]);

  return (
    <section id="pf" className="pf">
      <div className="container">
        <div className="pf__wrap">
          <div className="pf__head">
            <div>
              <span className="pf__kicker">Properties</span>
              <h1 className="pf__title">Browse Verified Listings</h1>
              <p className="pf__sub">
                Filter by location, type, budget, and bedrooms to find the right
                match.
              </p>
            </div>

            <div className="pf__chip">
              <FiSliders />
              <span>Smart Filters</span>
            </div>
          </div>

          <form className="pfBar" onSubmit={submit}>
            {/* Search */}
            <div className="pfField pfField--search">
              <label className="pfLabel">Search</label>
              <div className="pfInputWrap">
                <FiSearch className="pfIcon" />
                <input
                  className="pfInput"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="e.g. ‘Lake view’, ‘DHA Phase 6’, ‘2 bed’"
                />
              </div>
            </div>

            {/* Location */}
            <div className="pfField">
              <label className="pfLabel">Location</label>
              <select
                className="pfSelect"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              >
                <option>All</option>
                {locations?.map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </select>
            </div>

            {/* Type */}
            <div className="pfField">
              <label className="pfLabel">Type</label>
              <select
                className="pfSelect"
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                {TYPES.map((x) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
              </select>
            </div>

            {/* Price min */}
            <div className="pfField">
              <label className="pfLabel">Min Price</label>
              <input
                className="pfInput"
                inputMode="numeric"
                value={minPrice}
                onChange={(e) => setMinPrice(e.target.value)}
                placeholder="e.g. 20000000"
              />
            </div>

            {/* Price max */}
            <div className="pfField">
              <label className="pfLabel">Max Price</label>
              <input
                className="pfInput"
                inputMode="numeric"
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
                placeholder="e.g. 60000000"
              />
            </div>

            {/* Beds */}
            <div className="pfField">
              <label className="pfLabel">Beds</label>
              <select
                className="pfSelect"
                value={beds}
                onChange={(e) => setBeds(e.target.value)}
              >
                {BEDS.map((x) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort */}
            <div className="pfField">
              <label className="pfLabel">Sort</label>
              <select
                className="pfSelect"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
              >
                {SORTS.map((x) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
              </select>
            </div>

            {/* Actions */}
            <div className="pfActions">
              <button
                type="button"
                className="pfBtn pfBtn--ghost"
                onClick={reset}
              >
                <FiRefreshCw /> Reset
              </button>

              <button type="submit" className="pfBtn pfBtn--primary">
                <FiSearch /> Search
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Section to Scroll To */}
      <div ref={sectionRef}>
        {/* The section after the filter bar that you want to scroll to */}
        {/* <p>Results will be shown here...</p> */}
      </div>
    </section>
  );
}